// --- React
import { useCallback, useMemo, useState } from 'react';
// --- Utils & Config
import { usePermissions } from 'datastage/utils/permissions';
// --- Hooks
import { useProductGateData } from '../../../hooks/useProductGateData';
import { useProductGateColumns } from '../config/dynamicColumns';

/**
 * Custom hook for RightsLine Product Gate grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the Product Gate grid
 * - Fetches sorted product updates data from the API via useProductGateData
 * - Prepares column definitions with sorting controls based on user permissions and active tab
 *
 * @param {object} params - Optional configuration
 * @param {Array} params.columnHandlers - Handlers to pass to column definitions
 * @param {boolean} params.espnTitle - Whether ESPN+ tab is active (true = ESPN+, false = Content Sales)
 * @returns {object} { setSort, columns }
 */
export function useRLProductGateGridUtils({ columnHandlers = [], espnTitle = false } = {}) {
    const { fetchProductGates, updateProductGate, productGateRange } = useProductGateData();
    const [sortedColId, setSortedColId] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const { isRLProductGateUser, isRLProductGateAdmin } = usePermissions();

    // Get dynamic columns that adapt based on Hyperlane feature flag
    const dynamicColumnConfig = useProductGateColumns(
        columnHandlers[0], // onAddProducts
        columnHandlers[1], // onChangeIgnoreFlag
    );

    // Handle sorting: update state, fetch sorted data, update Product Gate
    const setSort = useCallback(
        async (colId, direction) => {
            setSortedColId(colId);
            setSortDirection(direction);

            const customSort = [colId, direction.toUpperCase()];
            const data = await fetchProductGates(productGateRange, null, customSort);

            if (data) {
                updateProductGate({
                    productUpdates: data?.content,
                    totalPages: data?.totalPages,
                    totalItems: data?.totalElements,
                    currentPage: data?.pageable?.pageNumber,
                    productGateSort: customSort,
                });
            }
        },
        [fetchProductGates, updateProductGate, productGateRange],
    );

    // Prepare column definitions with sorting controls based on user permissions and active tab
    // ESPN+ tab: Admin sees Action/Ignore columns
    // Content Sales tab: Content Sales role sees Action/Ignore columns
    const hasAddIgnoreControls = espnTitle ? isRLProductGateAdmin : isRLProductGateUser;
    const baseColumns = hasAddIgnoreControls
        ? dynamicColumnConfig.getAll()
        : dynamicColumnConfig.getLimited();

    const columns = useMemo(
        () =>
            baseColumns.map((col) => ({
                ...col,
                headerComponentParams: {
                    ...col.headerComponentParams,
                    setSort,
                    sortedColId,
                    sortDirection,
                },
            })),
        [setSort, sortedColId, sortDirection, baseColumns],
    );

    return { setSort, columns };
}
